import { Reveal, Overline, CTA } from "./primitives";
import { ENROLL_URL } from "../../config";

const lineItems = [
  {
    label: "13 core courses + 1 bonus course",
    sub: "Offered individually at $198 each",
    value: "$2,772",
  },
  {
    label: "Live group coaching",
    sub: "4 live calls every month · $150 per session",
    value: "$600/mo",
  },
  {
    label: "6 months of live coaching",
    sub: "$600 × 6 months",
    value: "$3,600",
  },
  {
    label: "12 months of live coaching",
    sub: "$600 × 12 months",
    value: "$7,200",
  },
];

const included = [
  "All 13 core courses + 1 bonus course",
  "4 live group coaching calls every month",
  "A community doing the work right beside you",
  "Founding rate locked in for as long as you remain a member",
  "No contracts. Cancel anytime.",
];

export const Pricing = () => (
  <section id="pricing" data-testid="pricing-section" className="bg-alabaster py-28 md:py-40">
    <div className="mx-auto max-w-[1100px] px-6 md:px-10">
      {/* Header */}
      <div className="mx-auto max-w-2xl text-center">
        <Reveal><Overline>The Investment</Overline></Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-6 font-display text-4xl font-medium leading-[1.02] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            What's this actually worth?
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 font-sans text-lg font-light leading-relaxed text-stonegray">
            Add it all up, and here's what this experience would cost piece by piece.
          </p>
        </Reveal>
      </div>

      {/* Stacked value receipt */}
      <Reveal>
        <div data-testid="pricing-receipt" className="mx-auto mt-16 max-w-2xl border border-ink/12 bg-ivory p-8 md:p-12">
          {lineItems.map((item) => (
            <div key={item.label} className="flex items-baseline justify-between gap-6 border-b border-ink/10 py-5">
              <div>
                <p className="font-display text-lg leading-snug text-ink md:text-xl">{item.label}</p>
                <p className="mt-1 font-sans text-sm font-light text-stonegray">{item.sub}</p>
              </div>
              <span className="shrink-0 font-display text-xl text-stonegray line-through decoration-terracotta/70 decoration-2 md:text-2xl">
                {item.value}
              </span>
            </div>
          ))}
          <div className="flex items-baseline justify-between gap-6 pt-7">
            <p className="font-display text-xl text-ink md:text-2xl">Total investment</p>
            <span data-testid="pricing-total-struck" className="shrink-0 font-display text-2xl text-ink line-through decoration-terracotta decoration-[3px] md:text-4xl">
              $6,372–$9,972
            </span>
          </div>
        </div>
      </Reveal>

      {/* Actual investment */}
      <div className="mt-14 grid items-stretch gap-0 overflow-hidden md:mt-20 md:grid-cols-12">
        <Reveal className="md:col-span-5" y={0}>
          <img
            src="/images/coaching.jpg"
            alt="Nichole coaching the community"
            data-testid="pricing-image"
            loading="lazy"
            className="h-64 w-full object-cover object-top md:h-full"
          />
        </Reveal>
        <div className="relative bg-ink p-10 text-ivory md:col-span-7 md:p-14">
          <div className="grain-overlay opacity-[0.06]" />
          <div className="relative">
            <Overline className="text-terracotta">Your Actual Investment</Overline>
            <div className="mt-6 flex flex-wrap items-baseline gap-x-4 gap-y-2">
              <span className="font-display text-2xl text-ivory/45 line-through decoration-terracotta/80 decoration-2 md:text-3xl">
                $6,372–$9,972
              </span>
              <span className="flex items-baseline gap-2">
                <span data-testid="pricing-actual-price" className="font-display text-6xl font-medium text-ivory md:text-7xl">$67</span>
                <span className="font-sans text-xl text-ivory/60">/month</span>
              </span>
            </div>
            <p className="mt-6 font-sans text-lg font-light text-ivory/70">Everything it comes with:</p>
            <ul className="mt-4 space-y-3 font-sans text-lg font-light text-ivory/90">
              {included.map((b) => (
                <li key={b} className="flex items-baseline gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-terracotta" />
                  {b}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <CTA href={ENROLL_URL} data-testid="pricing-cta">Join for $67/month</CTA>
            </div>
          </div>
        </div>
      </div>

      {/* Founding member pricing note */}
      <Reveal>
        <div className="mx-auto mt-16 max-w-3xl border-y border-ink/12 py-8 text-center">
          <span className="font-sans text-xs uppercase tracking-[0.24em] text-terracotta">Founding Member Pricing</span>
          <p className="mt-4 font-sans text-base font-light leading-relaxed text-stonegray">
            As the curriculum, coaching, and community grow, new members will pay <span className="text-ink">$97/month</span>.
            Join now and keep <span className="text-ink">$67/month for life.</span>
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.05}>
        <p className="mx-auto mt-10 max-w-2xl text-center font-display text-2xl italic leading-snug text-ink">
          As you do the simple math, you may realize how quickly one idea, one offer, or one decision could return
          this investment many times over.
        </p>
      </Reveal>
    </div>
  </section>
);
